import { type ReactElement, useReducer } from 'react';
import { useParams } from 'react-router';
import { Banner } from '#client/components/ui/Banner';
import { Page } from '#client/components/ui/Page';
import { PageLoading, PageMessage } from '#client/components/ui/PageStatus';
import { routes } from '#client/lib/routes';
import {
  type WizardStep,
  initialStep,
  isLocked,
  parseRunRoute,
  visibleStep,
  wizardReducer,
  wizardSubtitle,
  wizardTitle,
} from './import-wizard.logic';
import { CheckStep } from './steps/CheckStep';
import { CommitStep } from './steps/CommitStep';
import { MapStep } from './steps/MapStep';
import { UploadStep } from './steps/UploadStep';
import { type ImportRunState, useImportRun } from './useImportRun';
import { WizardActions } from './WizardActions';
import { WizardStepNav } from './WizardStepNav';
import { ButtonLink } from '#client/components/ui/ButtonLink';

/**
 * The import wizard.
 *
 * One route, four steps, and the step is component state rather than a URL
 * segment. Deliberate: a step is not a place — landing on `/imports/12/check`
 * from a bookmark would mean rendering a check for a mapping the visitor has
 * not seen, and every step already reads the same run. The run id *is* in the
 * URL, so the work is never lost.
 *
 * Steps 2 and 3 are reachable in both directions until the commit starts. Going
 * back and re-mapping is the normal case, not a mistake, and this is the screen
 * that has to make that obvious.
 */
export function ImportWizardPage() {
  const { id } = useParams<{ id: string }>();
  const route = parseRunRoute(id);
  const runId = route.kind === 'run' ? route.runId : null;

  const state = useImportRun(runId);
  const [chosen, dispatch] = useReducer(wizardReducer, runId, initialStep);

  if (route.kind === 'invalid') {
    return <PageMessage title="Import">There is no import with that id.</PageMessage>;
  }

  const status = state.run?.status;
  const step = visibleStep(chosen, status);
  const locked = isLocked(status);

  if (runId !== null && state.loading && !state.structure) {
    return <PageLoading title="Import" fullWidth />;
  }

  return (
    <Page
      title={wizardTitle(runId, state.run)}
      subtitle={wizardSubtitle(state.run)}
      backAction={
        <ButtonLink variant="secondary" size="md" to={routes.imports}>
          All imports
        </ButtonLink>
      }
      fullWidth
    >
      <WizardStepNav
        step={step}
        isDisabled={runId === null || locked}
        onSelect={(target) => dispatch({ type: 'go', step: target })}
      />

      {state.error && (
        <Banner tone="critical" onDismiss={state.clearError}>
          {state.error}
        </Banner>
      )}

      <StepBody step={step} state={state} />

      {runId !== null && !locked && (
        <WizardActions
          step={step}
          canCommit={state.summary !== null && state.run !== null}
          onBack={() => dispatch({ type: 'back' })}
          onForward={() => dispatch({ type: 'forward' })}
        />
      )}
    </Page>
  );
}

// Typed so a new step with no case here fails to compile instead of rendering nothing.
function StepBody({ step, state }: { step: WizardStep; state: ImportRunState }): ReactElement {
  switch (step) {
    case 'upload':
      return <UploadStep />;
    case 'map':
      return <MapStep state={state} />;
    case 'check':
      return <CheckStep state={state} />;
    case 'commit':
      return <CommitStep state={state} />;
  }
}
